import { Helmet } from "react-helmet-async";
import { BUSINESS, SERVICES, FAQS } from "@/lib/siteData";

export const SITE_URL = "https://repairpoint.in";
const OG_IMAGE = `${SITE_URL}/og-image.png`;

/**
 * Per-page SEO head manager. ... LocalBusiness JSON-LD here represents the
 * operating address (where customers visit) while the registered legal entity
 * is exposed via the Organization schema with a separate postal address.
 */
export default function Seo({
  title,
  description,
  path = "/",
  type = "website",
  image = OG_IMAGE,
  noindex = false,
  jsonLd = null,
}) {
  const url = `${SITE_URL}${path}`;
  const fullTitle = title.includes("Repair Point") ? title : `${title} | Repair Point`;
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Repair Point" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLd && (
        Array.isArray(jsonLd) ? jsonLd.map((j, i) => (
          <script key={i} type="application/ld+json">{JSON.stringify(j)}</script>
        )) : <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

/* ----------------- JSON-LD builders ----------------- */

export function localBusinessJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#business`,
    name: BUSINESS.name,
    image: `${SITE_URL}/favicon.svg`,
    url: SITE_URL,
    telephone: BUSINESS.phone,
    email: BUSINESS.email,
    priceRange: "$$",
    description:
      "Independent remote-only PC & Mac repair service for U.S. homes and small businesses. Virus & malware removal, slow-PC tune-ups, macOS troubleshooting, antivirus setup, data recovery and IT support — delivered over secure encrypted remote sessions.",
    address: {
      "@type": "PostalAddress",
      streetAddress: "2118 Glenridge Ct",
      addressLocality: "Marietta",
      addressRegion: "GA",
      postalCode: "30062",
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 33.9526,
      longitude: -84.5499,
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        opens: "10:00",
        closes: "19:30",
      },
    ],
    areaServed: [
      { "@type": "Country", name: "United States" },
      { "@type": "AdministrativeArea", name: "All 50 states" },
    ],
    sameAs: ["https://www.trustpilot.com/review/repairpoint.in"],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Repair Services",
      itemListElement: SERVICES.map((s) => ({
        "@type": "Offer",
        priceCurrency: "USD",
        price: s.price,
        itemOffered: {
          "@type": "Service",
          name: s.title,
          description: s.short,
        },
      })),
    },
  };
}

export function faqJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a,
      },
    })),
  };
}

export function breadcrumbJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
}

export function organizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: BUSINESS.name,
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.svg`,
    sameAs: ["https://www.trustpilot.com/review/repairpoint.in"],
    address: {
      "@type": "PostalAddress",
      streetAddress: "2118 Glenridge Ct",
      addressLocality: "Marietta",
      addressRegion: "GA",
      postalCode: "30062",
      addressCountry: "US",
    },
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: BUSINESS.phone,
        contactType: "customer support",
        email: BUSINESS.email,
        areaServed: ["IN", "US"],
        availableLanguage: ["English"],
      },
    ],
  };
}
